import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

import MissingCartData from '$src/components/MissingCartData';
import { ThemeApplicator } from '$src/components/ThemeApplicator';
import { DynamicThemeProvider } from '$src/context/ThemeContext';
import { useUserId } from '$src/hooks/client';
import { useAutoSelectPickupPoint } from '$src/hooks/client/useAutoSelectPickupPoint';
import { useCartCache } from '$src/hooks/client/useCartCache';
import { useGlobalPropertiesTracking } from '$src/hooks/client/useGlobalPropertiesTracking';
import { usePDDMetaobjectDiscount } from '$src/hooks/client/usePDDMetaobjectDiscount';
import { usePrefetchClickAndCollectStores } from '$src/hooks/client/usePrefetchClickAndCollectStores';
import { usePrefetchPickupPoints } from '$src/hooks/client/usePrefetchPickupPoints';
import { useRedirectSetData } from '$src/hooks/client/useRedirectSetData';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { usePrepareCart } from '$src/hooks/mutations';
import { useRecommendedDiscounts } from '$src/hooks/mutations/useRecommendDiscounts';
import { useCart, useShop, useUserLocationDetection } from '$src/hooks/queries';
import { useCartIdAtom, useCountryCodeAtom, useROFormMessageAtom } from '$src/hooks/state';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { setXLanguageGatewayHeader, setXSessionHeaders } from '$src/http';
import { decodeHtmlEntities } from '$src/utils';

interface InitWrapperProps {
  children: React.ReactNode;
}

export const InitWrapper = ({ children }: InitWrapperProps) => {
  useUserId();
  const router = useRouter();
  const message = useROFormMessageAtom();
  const { cartId } = useCartIdAtom();

  const isMissingCartData = useMemo(() => {
    if (!router.isReady) return false;
    if (router.pathname.startsWith('/design-playground')) return false;
    if (cartId) return false;
    if (router.query.data || router.query.passthrough) return false;
    return true;
  }, [router.isReady, router.pathname, cartId, router.query.data, router.query.passthrough]);

  const {
    mutateAsync: recommendDiscount,
    data: recommendedDiscounts,
    isPending: fetchingDiscount,
  } = useRecommendedDiscounts();
  const { data: shop } = useShop();
  const { data: cart, refetch } = useCart();
  const { track, identifyUserProperties } = useTrackAnalytics();
  const hasIdentifiedFeaturesRef = useRef(false);
  const hasTrackedInitRef = useRef(false);
  useRedirectSetData();
  useGlobalPropertiesTracking();
  useCartCache();

  useEffect(() => {
    identifyUserProperties({ language: message?.language ?? '-' });
  }, [message?.language, identifyUserProperties]);

  useEffect(() => {
    setXSessionHeaders();
  }, []);

  const { data: location } = useUserLocationDetection();
  const { onCountryCodeChange, country } = useCountryCodeAtom();

  useEffect(() => {
    onCountryCodeChange(location?.location.country.code);
  }, [location, onCountryCodeChange]);

  useEffect(() => {
    setXLanguageGatewayHeader(message?.language ?? 'fr');
  }, [message?.language]);

  const { mutateAsync: prepareCart, status: prepareCartStatus } = usePrepareCart();

  const { features, onCheckoutFeaturesChange } = useCheckoutFeaturesAtom();

  useEffect(() => {
    if (!shop) return;

    onCheckoutFeaturesChange(shop);
  }, [shop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasTrackedInitRef.current) {
      track(AnalyticsEventName.CHECKOUT_FORM_INIT, {
        page: router.pathname,
      });
      hasTrackedInitRef.current = true;
    }

    if (hasIdentifiedFeaturesRef.current || !shop || !features) return;
    const trackFeatures = features.reduce((result, feature) => ({ ...result, [feature]: 1 }), {});
    track(AnalyticsEventName.FEATURES_USED, {
      features: trackFeatures,
      hasBackgroundImage: Boolean(shop?.backgroundUrl),
    });
    hasIdentifiedFeaturesRef.current = true;
  }, [shop, features, track, router.pathname]);

  useEffect(() => {
    if (cart?.url) {
      const isMartinique = cart.shipping.address?.countryCode === 'MQ';
      if (cart.totalDuties || isMartinique) {
        window.location.href = cart.url;
      }
    }
  }, [cart]);

  useEffect(() => {
    if (
      !cartId &&
      message?.cart &&
      prepareCartStatus !== 'error' &&
      prepareCartStatus !== 'pending' &&
      prepareCartStatus !== 'success' &&
      country
    ) {
      prepareCart({
        cart: {
          ...message.cart,
          customerId: message.currentUser?.id,
          email: message.currentUser?.email,
          figpiiData: message.figpiiData,
          shippingAddress: message.currentUser?.address
            ? {
                firstName: decodeHtmlEntities(message.currentUser.firstName ?? ''),
                lastName: decodeHtmlEntities(message.currentUser.lastName ?? ''),
                address1: decodeHtmlEntities(message.currentUser.address.address1 ?? ''),
                city: decodeHtmlEntities(message.currentUser.address.city ?? ''),
                zip: decodeHtmlEntities(message.currentUser.address.zip ?? ''),
                country: decodeHtmlEntities(message.currentUser.address.country ?? ''),
                id: message.currentUser.address.id,
                phone: message.currentUser.phone ? decodeHtmlEntities(message.currentUser.phone) : undefined,
                address2: message.currentUser.address.address2
                  ? decodeHtmlEntities(message.currentUser.address.address2)
                  : undefined,
                province: message.currentUser.address.province
                  ? decodeHtmlEntities(message.currentUser.address.province)
                  : undefined,
                provinceCode: message.currentUser.address.province,
                countryCode: message.currentUser.address.countryCode ?? '',
              }
            : undefined,
        },
      });
    }
  }, [cartId, country, message, prepareCart, prepareCartStatus]);

  useEffect(() => {
    if (cart?.shipping?.ready === false || cart?.taxReady === false) {
      refetch();
    }
  }, [cart, refetch]);

  useEffect(() => {
    if (
      message?.cart.lineItems &&
      message.cart?.lineItems.length !== 0 &&
      prepareCartStatus !== 'error' &&
      prepareCartStatus !== 'pending' &&
      location?.location &&
      location?.location?.latitude !== 0 &&
      !recommendedDiscounts &&
      !fetchingDiscount &&
      shop?.features?.includes('recommended_discounts')
    ) {
      recommendDiscount({
        cart: message.cart,
      });
    }
  }, [message?.cart.lineItems, location, recommendDiscount, prepareCartStatus]);

  useAutoSelectPickupPoint();

  // Pre-warm both optimistic atoms as early as the cart is ready, mirroring
  // how `useAutoSelectPickupPoint` is mounted up here. Both hooks are
  // idempotent (the pickup one short-circuits on the atom being non-empty,
  // the C&C one only seeds method ids not already in the map), so the
  // duplicate mount with ShippingMethods is safe and gives us the earliest
  // possible atom-seeded display on segment switch.
  usePrefetchPickupPoints();
  usePrefetchClickAndCollectStores();

  //Custom hook to handle PDD metaobject discounts - INT-293
  usePDDMetaobjectDiscount();

  return (
    <DynamicThemeProvider>
      <ThemeApplicator />
      {children}
      <MissingCartData isOpen={isMissingCartData} />
    </DynamicThemeProvider>
  );
};
